import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DATE_FORMATS, MatDateFormats, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { MtxDatetimepickerModule } from '@ng-matero/extensions/datetimepicker';
import { provideMomentDatetimeAdapter } from '@ng-matero/extensions-moment-adapter';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatTabsModule } from '@angular/material/tabs';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';

enum ButtonTypes {
	CONFIRM = 'CONFIRM',
	CANCEL = 'CANCEL',
}

export const customMatDateFormat: MatDateFormats = {
	parse: {
		dateInput: 'YYYY-MM-DD',
	},
	display: {
		dateInput: 'YYYY-MM-DD',
		monthYearLabel: 'YYYY MMMM',
		dateA11yLabel: 'LL',
		monthYearA11yLabel: 'MMMM YYYY',
	},
};
@Component({
	selector: 'app-confirmation-dialog',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		FormsModule,
		MatCardModule,
		MatIconModule,
		MatButtonModule,
		MatDividerModule,
		MatSelectModule,
		TranslateModule,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatDialogModule,
		MtxDatetimepickerModule,
		MatTabsModule,
		CommonModule,
		MatCheckboxModule,
		MatRadioModule,
	],
	providers: [
		{
			provide: MAT_DATE_FORMATS,
			useValue: customMatDateFormat,
		},
		provideMomentDatetimeAdapter({
			parse: {
				dateInput: 'YYYY-MM-DD',
				monthInput: 'MMMM',
				yearInput: 'YYYY',
				timeInput: 'HH:mm',
				datetimeInput: 'YYYY-MM-DD HH:mm',
			},
			display: {
				dateInput: 'YYYY-MM-DD',
				monthInput: 'MMMM',
				yearInput: 'YYYY',
				timeInput: 'HH:mm',
				datetimeInput: 'YYYY-MM-DD HH:mm',
				monthYearLabel: 'YYYY MMMM',
				dateA11yLabel: 'LL',
				monthYearA11yLabel: 'MMMM YYYY',
				popupHeaderDateLabel: 'MMM DD, ddd',
			},
		}),
	],
	templateUrl: './confirmation-dialog.component.html',
	styleUrl: './confirmation-dialog.component.scss',
})
export class ConfirmationDialogComponent {
	dateRangePicker: FormGroup = new FormGroup({
		dateFrom: new FormControl(),
		dateTo: new FormControl(),
	});

	selectFormControl: FormControl = new FormControl();

	dateTimeFormControl: FormControl = new FormControl();
	dateTimeFormControls: { [key: string]: FormControl } = {};

	textarea: FormControl = new FormControl();

	textfield: FormControl = new FormControl();

	findTextControl: FormControl = new FormControl();
	replaceTextControl: FormControl = new FormControl();

	selectedFiles: { [key: string]: File } = {};

	htmlContent!: SafeHtml;

	tabForms: FormGroup[] = [];

	fieldControls: { [key: string]: FormControl } = {};

	activeTabIndex = 0;

	dateRangeError: string | null = null;
	dateRangeErrorParams: { [key: string]: any } = {};

	constructor(public dialogRef: MatDialogRef<ConfirmationDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: any, private sanitizer: DomSanitizer, private fb: FormBuilder, private router: Router) {
		this.setupFieldWatchers();
		this.initializeFieldControls();
		if (data && data.type === 'custom-fields') {
			this.data.fields.forEach((field: any) => {
				switch (field.type) {
					case 'date-range': {
						this.dateRangePicker.controls['dateFrom'].setValue(new Date(field.data[0].value[0].setHours(0, 0, 0)));
						this.dateRangePicker.controls['dateTo'].setValue(new Date(field.data[0].value[1].setHours(0, 0, 0)));
						break;
					}
					case 'selection': {
						const defaultValue = field.data.find((item: any) => item.default);
						if (defaultValue) {
							this.selectFormControl.setValue(defaultValue.value);
						}
						break;
					}
					case 'textarea': {
						const defaultValueTextarea = field.data;
						if (defaultValueTextarea) {
							this.textarea.setValue(defaultValueTextarea.join(', '));
						}
						break;
					}
					case 'rawHtml': {
						const rawHtml = field.data;
						this.htmlContent = this.sanitizer.bypassSecurityTrustHtml(rawHtml);
						break;
					}
					case 'date': {
						if (field.data) {
							this.dateTimeFormControl.setValue(field.data);
						}
						break;
					}
				}
			});
		}
		if (this.data && this.data.tabs) {
      		this.initializeTabForms();
    	}
		if (this.data && this.data.defaultTab !== undefined) {
			this.activeTabIndex = this.data.defaultTab;
		}
	}

	public buttonAction(data: any): void {
		switch (this.data.type) {
			case 'custom-fields': {
				data = {};
				this.data.fields.forEach((field: any) => {
					if (field.type === 'date-range') {
						data.dateFrom = this.dateRangePicker.controls['dateFrom'].value.toDate?.();
						data.dateTo = this.dateRangePicker.controls['dateTo'].value;
						data.dateTo = new Date(new Date(data.dateTo).setDate(new Date(data.dateTo).getDate() + 1));
					} else if (field.type === 'selection-multiple') {
						data[field.key || 'selection'] = this.selectFormControl.value;
					} else if (field.type === 'selection') {
						data[field.key || 'singleSelection'] = this.fieldControls[field.key]?.value || this.selectFormControl.value;
					} else if (field.type === 'radio') {
						data[field.key] = this.fieldControls[field.key]?.value;
					} else if (field.type === 'checkbox') {
						data[field.key] = this.fieldControls[field.key]?.value || false;
					} else if (field.type === 'date-time') {
						const control = field.key && this.dateTimeFormControls[field.key] ? this.dateTimeFormControls[field.key] : this.dateTimeFormControl;
						data[field.key || 'dateTime'] = control.value ? new Date(control.value.toDate()) : null;
					} else if (field.type === 'date') {
						const control = field.key ? this.getDateTimeControl(field.key) : this.dateTimeFormControl;
						data[field.key || 'dateTime'] = control.value ? new Date(control.value) : null;
					} else if (field.type === 'textarea') {
						data[field.key || 'textarea'] = this.textarea.value;
					} else if (field.type === 'textfield') {
						data[field.key || 'textfield'] = this.getTextFieldControl(field.key || 'textfield').value;
					} else if (field.type === 'file') {
						data[field.key] = this.selectedFiles[field.key] || null;
					}
				});
				break;
			}
			case 'custom-tabs': {
				data = {};
				if (this.tabForms && this.tabForms[this.activeTabIndex]) {
					const formValues = this.tabForms[this.activeTabIndex].value;
					Object.keys(formValues).forEach(key => {
						data[key] = formValues[key];
					});
				}
				break;
			}
		}
		this.dialogRef.close({ data, activeTab: this.activeTabIndex });
	}

	public messageVisibility() {
		switch (this.data.type) {
			case 'translate-confirmation':
			case 'send-confirmation':
			case 'delete-confirmation': {
				return true;
			}
			default: {
				return this.data.message && this.data.mode !== 'EXPORT';
			}
		}
	}

	public getMessageText() {
		switch (this.data.type) {
			case 'translate-confirmation':
			case 'send-confirmation':
			case 'delete-confirmation': {
				return 'NOTIFICATIONS.' + this.data.type.replaceAll('-', '_').toUpperCase();
			}
		}
		return this.data.message;
	}

	public buttonVisibility(buttonType: string) {
		switch (this.data.type) {
			case 'translate-confirmation':
			case 'send-confirmation':
			case 'delete-confirmation':
			case 'custom-fields':
			case 'custom-tabs': {
				return this.checkButtonType(buttonType, ButtonTypes.CONFIRM) || this.checkButtonType(buttonType, ButtonTypes.CANCEL);
			}
			case 'message': {
				return this.checkButtonType(buttonType, ButtonTypes.CONFIRM);
			}
			default: {
				return false;
			}
		}
	}
	private checkButtonType(buttonType: string, allowedButtonType: string) {
		return buttonType === allowedButtonType;
	}

	private initializeTabForms() {
		this.tabForms = this.data.tabs.map((tab: any) => {
			const group: { [key: string]: FormControl } = {};
			tab.fields.forEach((field: any) => {
				group[field.key] = new FormControl(field.value || '');
			});
			return this.fb.group(group);
		});
	}

	getFormControl(tabIndex: number, key: string): FormControl {
		return this.tabForms[tabIndex].get(key) as FormControl;
	}

	isTabFieldVisible(tabIndex: number, field: any): boolean {
		if (!field.showWhen) {
			return true;
		}
		const control = this.tabForms[tabIndex]?.get(field.showWhen.key);
		if (!control) {
			return false;
		}
		return (field.showWhen.values || []).includes(control.value);
	}

	getTextFieldControl(key: string): FormControl {
		switch (key) {
			case 'findText':
				return this.findTextControl;
			case 'replaceText':
				return this.replaceTextControl;
			case 'daysBehind':
				return this.getFieldControl(key);
			default:
				return this.textfield;
		}
	}

	triggerFileInput(key: string) {
		const fileInput = document.getElementById('file-' + key) as HTMLInputElement;
		if (fileInput) {
			fileInput.click();
		}
	}

	onFileSelected(event: any, key: string) {
		const file = event.target.files[0];
		if (file) {
			this.selectedFiles[key] = file;
			if (key === 'file' && this.data?.type === 'custom-fields') {
				this.textfield.disable();
			}
		} else {
			delete this.selectedFiles[key];
			if (key === 'file' && this.data?.type === 'custom-fields' && !this.textfield.value?.trim()) {
				this.textfield.enable();
			}
		}
	}

	getSelectedFileName(key: string): string {
		return this.selectedFiles[key]?.name || '';
	}

	private setupFieldWatchers() {
		this.textfield.valueChanges.subscribe(value => {
			const hasUrl = value && value.trim().length > 0;
			if (hasUrl) {
				const fileInput = document.getElementById('file-file') as HTMLInputElement;
				if (fileInput) {
					fileInput.value = '';
				}
				delete this.selectedFiles['file'];
				this.enableFileInputElement(false);
			} else if (!this.selectedFiles['file']) {
				this.enableFileInputElement(true);
			}
		});
	}

	isFieldDisabled(field: any): boolean {
		return false;
	}

	private enableFileInputElement(enable: boolean) {
		const fileInput = document.getElementById('file-file') as HTMLInputElement;
		if (fileInput) {
			fileInput.disabled = !enable;
		}
	}

	isFormValid(): boolean {
		if (this.data?.skipFormValidation) {
			// Check if all required date fields have values
			if (this.data?.fields) {
				for (const field of this.data.fields) {
					if (field.required && field.type === 'date' && field.key) {
						const control = this.getDateTimeControl(field.key);
						if (!control.value) {
							return false;
						}
					}
					if (field.required && field.type === 'selection' && field.key) {
						const control = this.getFieldControl(field.key);
						if (!control.value) {
							return false;
						}
					}
				}
			}
			// Check for date range errors
			if (this.dateRangeError) {
				return false;
			}
			return true;
		}

		if (this.data?.mode === 'EXPORT' && this.data?.type === 'custom-fields') {
			return !!(this.fieldControls['format']?.value);
		}

		if (this.data?.type === 'custom-fields' && this.router.url.includes('transcriptions') && !this.data.skipTranscriptionValidation) {
			const hasLanguage = this.selectFormControl.value;

			const hasUrl = this.textfield.value && this.textfield.value.trim();
			const hasFile = !!this.selectedFiles['file'];
			const hasInput = hasUrl || hasFile;

			return hasLanguage && hasInput;
		}

		if (this.data?.type === 'custom-fields' && this.data.skipTranscriptionValidation) {
			const findText = this.findTextControl.value && this.findTextControl.value.trim();
			return !!findText;
		}

		return true;
	}

	closeDialog() {
		this.dialogRef.close();
	}

	private initializeFieldControls() {
		if (this.data && this.data.fields) {
			this.data.fields.forEach((field: any) => {
				if (field.type === 'selection' || field.type === 'checkbox' || field.type === 'radio' || field.type === 'textfield') {
					let defaultValue;
					if (field.type === 'selection') {
						defaultValue = field.data?.find((item: any) => item.default)?.value;
					} else if (field.type === 'checkbox') {
						defaultValue = field.default || false;
					} else if (field.type === 'radio') {
						defaultValue = field.defaultValue || field.options?.[0];
					} else if (field.type === 'textfield' && field.key !== 'textfield') {
						defaultValue = field.defaultValue || '';
					}
					if (field.key && (field.type !== 'textfield' || field.key !== 'textfield')) {
						this.fieldControls[field.key] = new FormControl(defaultValue);
					}
				}
			});

			if (this.fieldControls['mergeText']) {
				this.fieldControls['mergeText'].valueChanges.subscribe(mergeTextValue => {
					if (mergeTextValue) {
						if (this.fieldControls['includeSpeakers']) {
							this.fieldControls['includeSpeakers'].setValue(false);
						}
						if (this.fieldControls['includeTimestamps']) {
							this.fieldControls['includeTimestamps'].setValue(false);
						}
					}
				});
			}

			// Watch for format changes and reset fields when SRT is selected
			if (this.fieldControls['format']) {
				this.fieldControls['format'].valueChanges.subscribe(formatValue => {
					if (formatValue === 'SRT') {
						// SRT files always include timestamps and don't support merge text
						if (this.fieldControls['includeTimestamps']) {
							this.fieldControls['includeTimestamps'].setValue(false);
						}
						if (this.fieldControls['mergeText']) {
							this.fieldControls['mergeText'].setValue(false);
						}
					}
				});
			}

			if (this.fieldControls['country']) {
				this.fieldControls['country'].valueChanges.subscribe(() => {
					if (this.fieldControls['noticeType']) {
						this.fieldControls['noticeType'].setValue(null);
					}
				});
			}

			// Set up date range validation
			this.data.fields.forEach((field: any) => {
				if (field.type === 'date' && field.maxDaysFrom && field.maxDaysLimit) {
					const toControl = this.getDateTimeControl(field.key);
					const fromControl = this.getDateTimeControl(field.maxDaysFrom);

					const checkDateRange = () => {
						const fromDate = fromControl.value;
						const toDate = toControl.value;

						if (fromDate && toDate) {
							const diffInMs = Math.abs(new Date(toDate).getTime() - new Date(fromDate).getTime());
							const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

							if (diffInDays > field.maxDaysLimit) {
								this.dateRangeError = 'ASSORTED.DATE_RANGE_LIMIT_MESSAGE';
								this.dateRangeErrorParams = { ALLOWED_DAYS_LIMIT: field.maxDaysLimit };
							} else {
								this.dateRangeError = null;
							}
						} else {
							this.dateRangeError = null;
						}
					};

					toControl.valueChanges.subscribe(() => checkDateRange());
					fromControl.valueChanges.subscribe(() => checkDateRange());
				}
			});

			// Seeded after the range watchers are wired, so a default range that already exceeds
			// the limit shows the error straight away instead of on the first manual change.
			this.data.fields.forEach((field: any) => {
				if (field.type === 'date' && field.key && field.defaultValue) {
					this.getDateTimeControl(field.key).setValue(field.defaultValue);
				}
			});
		}
	}

	getFieldControl(key: string): FormControl {
		if (!this.fieldControls[key]) {
			this.fieldControls[key] = new FormControl();
		}
		return this.fieldControls[key];
	}

	getDateTimeControl(key?: string): FormControl {
		if (!key) {
			return this.dateTimeFormControl;
		}
		if (!this.dateTimeFormControls[key]) {
			this.dateTimeFormControls[key] = new FormControl();
		}
		return this.dateTimeFormControls[key];
	}

	onRadioChange(key: string, value: string) {
		if (this.fieldControls[key]) {
			this.fieldControls[key].setValue(value);
		}
	}

	getSelectionData(field: any): any[] {
		if (field.kosovoData && this.fieldControls['country']) {
			const selectedCountry = this.fieldControls['country'].value;
			return selectedCountry === 'xk' ? field.kosovoData : field.data;
		}
		return field.data;
	}

	isFieldVisible(field: any): boolean {
		if (field.countrySpecific && this.fieldControls['country']) {
			const selectedCountry = this.fieldControls['country'].value;
			return selectedCountry === field.countrySpecific;
		}
		return true;
	}
}
